import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/adminApi";
import { mediaUrl } from "../../lib/content";

// Upload + preview field. value is a storage path ("luminart/...") or URL.
const MediaUpload = ({ label, value, onChange, kind = "image", testId }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await adminApi.post("/admin/upload", form);
      onChange(data.path);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const url = value ? mediaUrl(value) : null;

  return (
    <div data-testid={testId}>
      <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
        {label}
      </span>
      {url ? (
        <div className="relative border border-white/15 bg-black/40 p-2">
          {kind === "video" ? (
            <video src={url} controls muted playsInline className="w-full max-h-56 object-contain bg-black" />
          ) : (
            <img src={url} alt={label} className="w-full max-h-48 object-contain bg-black" />
          )}
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid={`${testId}-remove`}
            className="absolute top-2 right-2 bg-black/70 border border-white/20 p-1.5 text-white/70 hover:text-white"
            aria-label={`Remove ${label}`}
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testId}-button`}
          className="flex w-full items-center justify-center gap-2 border border-dashed border-white/20 py-6 text-[11px] uppercase tracking-[0.2em] text-white/50 hover:border-white/50 hover:text-white transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {busy ? "Uploading…" : `Upload ${kind}`}
        </button>
      )}
      {url && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testId}-replace`}
          className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#c8b99d]/80 hover:text-[#c8b99d]"
        >
          {busy ? "Uploading…" : "Replace"}
        </button>
      )}
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={kind === "video" ? "video/mp4,video/webm,video/quicktime" : "image/jpeg,image/png,image/webp,image/gif"}
        className="hidden"
        onChange={pick}
      />
    </div>
  );
};

export default MediaUpload;
